/**
 * Lastgang-Parser — extrahiert aus FileUpload.jsx
 * Parst CSV und Excel-Dateien mit flexibler Spaltenerkennung
 */
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const HEADER_MAP = {
  'zeitstempel': 'timestamp',
  'timestamp': 'timestamp',
  'datum': 'date',
  'date': 'date',
  'uhrzeit': 'time',
  'time': 'time',
  'zeit': 'time',
  'quarter': 'time',
  'quarterstunde': 'time',
  'viertelstunde': 'time',
  'datetime': 'timestamp',
  'verbrauch': 'consumption',
  'energie': 'consumption',
  'kwh': 'consumption',
  'wert': 'consumption',
  'value': 'consumption',
  'leistung': 'consumption',
  'consumption': 'consumption',
  'power': 'consumption',
  'menge': 'consumption',
  'energy': 'consumption',
  'kw': 'consumption'
};

export const parseCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => resolve(results.data),
      error: (error) => reject(error)
    });
  });
};

export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        resolve(jsonData);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsBinaryString(file);
  });
};

export const parseTimestamp = (timestampStr) => {
  if (!timestampStr) return null;
  const str = String(timestampStr).trim();

  // ISO-Format
  const date = new Date(str);
  if (!isNaN(date.getTime()) && date.getFullYear() > 1900) return date;

  // Deutsches Format: DD.MM.YYYY HH:MM
  if (str.includes('.') && str.includes(':')) {
    const parts = str.split(' ');
    if (parts.length >= 2) {
      const dateParts = parts[0].split('.');
      const timeParts = parts[1].split(':');
      if (dateParts.length === 3 && timeParts.length >= 2) {
        const parsedDate = new Date(
          parseInt(dateParts[2]),
          parseInt(dateParts[1]) - 1,
          parseInt(dateParts[0]),
          parseInt(timeParts[0]),
          parseInt(timeParts[1]),
          timeParts.length > 2 ? parseInt(timeParts[2]) : 0
        );
        if (!isNaN(parsedDate.getTime())) return parsedDate;
      }
    }
  }

  // Nur Zeit: HH:MM
  if (str.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
    const timeParts = str.split(':');
    const today = new Date();
    const timeDate = new Date(
      today.getFullYear(), today.getMonth(), today.getDate(),
      parseInt(timeParts[0]), parseInt(timeParts[1]),
      timeParts.length > 2 ? parseInt(timeParts[2]) : 0
    );
    if (!isNaN(timeDate.getTime())) return timeDate;
  }

  // Generischer Fallback
  const separators = [' ', 'T', '\t'];
  for (const sep of separators) {
    if (str.includes(sep)) {
      const parts = str.split(sep);
      if (parts.length >= 2) {
        const fallbackDate = new Date(parts[0] + ' ' + parts[1]);
        if (!isNaN(fallbackDate.getTime()) && fallbackDate.getFullYear() > 1900) return fallbackDate;
      }
    }
  }
  throw new Error(`Unbekanntes Zeitstempel-Format: ${str}`);
};

export const parseLoadProfile = (rawData) => {
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    throw new Error('Ungültige oder leere Datendatei');
  }

  let headerRowIndex = -1;
  let headers = [];
  let metadata = {};

  for (let i = 0; i < Math.min(rawData.length, 25); i++) {
    const row = rawData[i];
    if (!row || row.length < 2) continue;

    const normalizedRow = row.map(cell =>
      String(cell || '').toLowerCase().trim().replace(/[\[\]()]/g, '').replace(/\s+/g, '_')
    );

    let hasTimestamp = false;
    let hasDate = false;
    let hasTime = false;
    let hasConsumption = false;

    normalizedRow.forEach(cell => {
      const cleanCell = cell.replace(/_.*$/, '');
      if (['zeitstempel', 'timestamp', 'datetime'].includes(cleanCell)) hasTimestamp = true;
      if (['datum', 'date'].includes(cleanCell)) hasDate = true;
      if (['uhrzeit', 'time', 'zeit', 'quarter', 'quarterstunde', 'viertelstunde'].includes(cleanCell)) hasTime = true;
      if (['verbrauch', 'energie', 'kwh', 'consumption', 'power', 'value', 'leistung', 'wert', 'menge', 'energy', 'kw'].includes(cleanCell)) hasConsumption = true;
    });

    if ((hasTimestamp || (hasDate && hasTime)) && hasConsumption) {
      headerRowIndex = i;
      headers = normalizedRow;
      break;
    }

    if (row.length >= 2) {
      const key = String(row[0] || '').trim();
      const value = String(row[1] || '').trim();
      if (key && value && key.length < 50) metadata[key] = value;
    }
  }

  if (headerRowIndex === -1) {
    throw new Error('Keine gültige Header-Zeile mit Zeitstempel und Verbrauchsdaten gefunden');
  }

  const mappedHeaders = headers.map(header => {
    const cleanHeader = header.replace(/_.*$/, '');
    return HEADER_MAP[cleanHeader] || header;
  });

  const timestampIndex = mappedHeaders.findIndex(h => h === 'timestamp');
  const dateIndex = mappedHeaders.findIndex(h => h === 'date');
  const timeIndex = mappedHeaders.findIndex(h => h === 'time');
  const consumptionIndex = mappedHeaders.findIndex(h => h === 'consumption');

  if (timestampIndex === -1 && (dateIndex === -1 || timeIndex === -1)) {
    throw new Error('Keine Zeitstempel-Spalte gefunden');
  }
  if (consumptionIndex === -1) {
    throw new Error('Keine Verbrauchsspalte gefunden');
  }

  const processedData = [];
  for (let i = headerRowIndex + 1; i < rawData.length; i++) {
    const row = rawData[i];
    if (!row || row.length < Math.max(timestampIndex, dateIndex, timeIndex, consumptionIndex) + 1) continue;

    try {
      let timestamp = null;
      if (timestampIndex !== -1 && row[timestampIndex]) {
        timestamp = parseTimestamp(String(row[timestampIndex]));
      } else if (dateIndex !== -1 && timeIndex !== -1 && row[dateIndex] && row[timeIndex]) {
        timestamp = parseTimestamp(`${String(row[dateIndex]).trim()} ${String(row[timeIndex]).trim()}`);
      }

      const consumptionStr = String(row[consumptionIndex] || '').replace(',', '.');
      const consumptionValue = parseFloat(consumptionStr);

      if (timestamp && !isNaN(consumptionValue)) {
        processedData.push({ time: timestamp, value: consumptionValue });
      }
    } catch (error) {
      // Überspringe fehlerhafte Zeilen
    }
  }

  if (processedData.length === 0) {
    throw new Error('Keine gültigen Datenzeilen gefunden');
  }

  const totalKwh = processedData.reduce((sum, d) => sum + d.value, 0);

  // Peak/Off-Peak Analyse
  let peakKwh = 0;
  let offPeakKwh = 0;
  processedData.forEach(d => {
    const hour = d.time.getHours();
    if (hour >= 8 && hour < 20) {
      peakKwh += d.value;
    } else {
      offPeakKwh += d.value;
    }
  });

  return {
    data: processedData,
    metadata,
    totalKwh,
    peakKwh,
    offPeakKwh,
    dataPoints: processedData.length,
    intervalMinutes: processedData.length > 1
      ? Math.round((processedData[1].time - processedData[0].time) / 60000)
      : 15
  };
};

export const parseFile = async (file) => {
  const extension = file.name.split('.').pop().toLowerCase();
  let rawData;

  if (['csv', 'txt'].includes(extension)) {
    rawData = await parseCSVFile(file);
  } else if (['xlsx', 'xls'].includes(extension)) {
    rawData = await parseExcelFile(file);
  } else {
    throw new Error(`Nicht unterstütztes Dateiformat: .${extension}`);
  }

  return parseLoadProfile(rawData);
};
